import { useRef, useCallback, useEffect } from 'react';
import axios from 'axios';

const API = `${window.location.origin}/api`;

/**
 * AI Reading Accuracy Tracker (Enhanced Module 1)
 * Tracks: scroll position/speed, time per page, mouse movement, touch,
 * page visibility, idle detection, page revisits, rapid flip detection.
 * Events are batched and sent asynchronously every 10 seconds.
 * Supports periodic verified progress refresh.
 */
export function useReadingTracker({ bookId, chapterId, token, enabled = true, onProgressUpdate }) {
  const eventsBuffer = useRef([]);
  const lastScrollY = useRef(0);
  const lastScrollTime = useRef(Date.now());
  const pageStartTime = useRef(Date.now());
  const isIdle = useRef(false);
  const idleTimer = useRef(null);
  const flushInterval = useRef(null);
  const progressInterval = useRef(null);
  const mouseActive = useRef(true);
  const mouseTimer = useRef(null);
  const currentPage = useRef(1);
  const totalPages = useRef(100);
  const tabActive = useRef(true);
  const contentVisible = useRef(true);
  const visitedPages = useRef(new Set());

  // Anti-cheat: rapid flip tracking
  const lastPageChangeTime = useRef(Date.now());
  const rapidFlipCount = useRef(0);

  // Page estimation from reading time (for PDF iframe where page detection is impossible)
  const activeReadingSeconds = useRef(0);
  const startPage = useRef(1);
  const SECONDS_PER_PAGE = 180; // ~3 minutes per page — conservative to avoid inflating progress

  const getDeviceType = () => {
    const w = window.innerWidth;
    if (w < 768) return 'mobile';
    if (w < 1024) return 'tablet';
    return 'desktop';
  };

  const addEvent = useCallback((type, extraData = {}) => {
    if (!enabled || !bookId) return;

    const now = Date.now();
    const timeOnPage = (now - pageStartTime.current) / 1000;
    const iframe = document.querySelector('iframe');
    let scrollDepth = 0;

    try {
      if (iframe?.contentDocument) {
        const doc = iframe.contentDocument;
        const scrollTop = doc.documentElement.scrollTop || doc.body.scrollTop;
        const scrollHeight = doc.documentElement.scrollHeight || doc.body.scrollHeight;
        const clientHeight = doc.documentElement.clientHeight || doc.body.clientHeight;
        scrollDepth = scrollHeight > clientHeight
          ? Math.min(100, (scrollTop / (scrollHeight - clientHeight)) * 100)
          : 100;
      } else if (
        document.querySelector('.pdf-flipbook') ||
        document.querySelector('[data-testid="reader-flipbook"]')
      ) {
        // FlipbookReader (canvas-based) has no scrolling — each page is
        // rendered whole inside a <canvas> and flipped, not scrolled.
        // The backend verifier requires max_scroll >= 30 to consider a
        // page "read", so events emitted while inside the flipbook must
        // report full depth. Otherwise every page is unverifiable and
        // the progress bar stays stuck at 0% no matter how long the
        // reader spends on the page. (Root cause of the "0% stuck"
        // bug reported 2026-07-26.)
        scrollDepth = 100;
      }
    } catch {
      // Cross-origin iframe
    }

    const timeDelta = (now - lastScrollTime.current) / 1000;
    const scrollDelta = Math.abs(scrollDepth - lastScrollY.current);
    const scrollSpeed = timeDelta > 0 ? (scrollDelta / timeDelta) * 10 : 0;

    const isRevisit = visitedPages.current.has(currentPage.current);
    visitedPages.current.add(currentPage.current);

    eventsBuffer.current.push({
      book_id: bookId,
      chapter_id: chapterId || null,
      page_number: currentPage.current,
      total_pages: totalPages.current,
      scroll_depth: Math.round(scrollDepth * 10) / 10,
      scroll_speed: Math.round(scrollSpeed * 10) / 10,
      time_on_page: Math.round(timeOnPage * 10) / 10,
      interaction_type: type,
      tab_active: tabActive.current,
      device_type: getDeviceType(),
      is_idle: isIdle.current,
      mouse_active: mouseActive.current,
      content_visible: contentVisible.current,
      ...extraData
    });

    lastScrollY.current = scrollDepth;
    lastScrollTime.current = now;
  }, [bookId, chapterId, enabled]);

  // Flush events to backend
  const flushEvents = useCallback(async () => {
    if (eventsBuffer.current.length === 0 || !token) return;

    const batch = [...eventsBuffer.current];
    eventsBuffer.current = [];

    try {
      await axios.post(`${API}/reading-events/batch`, { events: batch }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch {
      // Re-add failed events
      eventsBuffer.current = [...batch, ...eventsBuffer.current];
    }
  }, [token]);

  // Fetch verified progress from backend
  const fetchVerifiedProgress = useCallback(async () => {
    if (!token || !bookId) return null;
    try {
      const res = await axios.get(`${API}/user-progress/${bookId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (onProgressUpdate) onProgressUpdate(res.data);
      return res.data;
    } catch {
      return null;
    }
  }, [token, bookId, onProgressUpdate]);

  // Reset idle timer
  const resetIdle = useCallback(() => {
    isIdle.current = false;
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => {
      isIdle.current = true;
      addEvent('idle');
    }, 30000); // 30s idle threshold
  }, [addEvent]);

  // Mouse activity tracking
  const handleMouseMove = useCallback(() => {
    mouseActive.current = true;
    if (mouseTimer.current) clearTimeout(mouseTimer.current);
    mouseTimer.current = setTimeout(() => {
      mouseActive.current = false;
    }, 5000);
    resetIdle();
  }, [resetIdle]);

  // Scroll tracking
  const handleScroll = useCallback(() => {
    addEvent('scroll');
    resetIdle();
  }, [addEvent, resetIdle]);

  // Click tracking
  const handleClick = useCallback(() => {
    addEvent('click');
    resetIdle();
  }, [addEvent, resetIdle]);

  // Touch tracking
  const handleTouch = useCallback(() => {
    addEvent('touch');
    resetIdle();
  }, [addEvent, resetIdle]);

  // Visibility change
  const handleVisibility = useCallback(() => {
    tabActive.current = !document.hidden;
    contentVisible.current = !document.hidden;
    addEvent(document.hidden ? 'tab_hidden' : 'tab_visible');
  }, [addEvent]);

  // Set page info with rapid flip detection
  const setPageInfo = useCallback((page, total) => {
    const now = Date.now();
    const timeSinceLastChange = (now - lastPageChangeTime.current) / 1000;

    // Detect rapid page flipping (< 3 seconds between page changes)
    if (page !== currentPage.current && timeSinceLastChange < 3) {
      rapidFlipCount.current += 1;
    } else {
      rapidFlipCount.current = Math.max(0, rapidFlipCount.current - 1);
    }

    // Track the initial/resume page for time-based estimation
    if (page > startPage.current) {
      startPage.current = page;
      activeReadingSeconds.current = 0; // Reset time counter from new start
    }

    currentPage.current = page;
    totalPages.current = total;
    pageStartTime.current = now;
    lastPageChangeTime.current = now;
    addEvent('page_view', { rapid_flip: rapidFlipCount.current > 3 });
  }, [addEvent]);

  // Consume view credit on book open
  const consumeViewCredit = useCallback(async () => {
    if (!token || !bookId) return;
    try {
      const res = await axios.post(
        `${API}/consume-view-credit?book_id=${bookId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return res.data;
    } catch {
      return null;
    }
  }, [token, bookId]);

  useEffect(() => {
    if (!enabled) return;

    // Start periodic flush (every 8 seconds for real-time progress)
    flushInterval.current = setInterval(() => {
      // Estimate page progression from active reading time
      // (PDF iframe doesn't expose which page the user is on)
      if (!isIdle.current && tabActive.current && contentVisible.current) {
        activeReadingSeconds.current += 8;
        const estimatedPage = Math.min(
          startPage.current + Math.floor(activeReadingSeconds.current / SECONDS_PER_PAGE),
          totalPages.current
        );
        if (estimatedPage > currentPage.current) {
          currentPage.current = estimatedPage;
          pageStartTime.current = Date.now();
          addEvent('page_view');
        }
      }
      flushEvents();
    }, 8000);

    // Start periodic verified progress refresh (every 10 seconds for real-time updates)
    // Flush first, then fetch progress so backend has latest events
    progressInterval.current = setInterval(async () => {
      await flushEvents();
      await fetchVerifiedProgress();
    }, 10000);

    // Add event listeners
    document.addEventListener('mousemove', handleMouseMove, { passive: true });
    document.addEventListener('click', handleClick, { passive: true });
    document.addEventListener('touchstart', handleTouch, { passive: true });
    document.addEventListener('visibilitychange', handleVisibility);

    // Try to attach scroll listener to iframe
    const attachIframeListeners = () => {
      const iframe = document.querySelector('iframe');
      if (iframe?.contentDocument) {
        try {
          iframe.contentDocument.addEventListener('scroll', handleScroll, { passive: true });
          iframe.contentDocument.addEventListener('click', handleClick, { passive: true });
          iframe.contentDocument.addEventListener('mousemove', handleMouseMove, { passive: true });
        } catch {
          // Cross-origin
        }
      }
    };

    // Retry iframe attachment
    const iframeCheck = setInterval(attachIframeListeners, 2000);

    // Initial idle timer
    resetIdle();

    // Record initial page view
    addEvent('page_view');

    // Fetch initial progress after a short delay
    setTimeout(fetchVerifiedProgress, 2000);

    return () => {
      clearInterval(flushInterval.current);
      clearInterval(progressInterval.current);
      clearInterval(iframeCheck);
      clearTimeout(idleTimer.current);
      clearTimeout(mouseTimer.current);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('click', handleClick);
      document.removeEventListener('touchstart', handleTouch);
      document.removeEventListener('visibilitychange', handleVisibility);

      // Flush remaining events on unmount
      flushEvents();
    };
  }, [enabled, handleMouseMove, handleClick, handleTouch, handleVisibility, handleScroll, addEvent, flushEvents, resetIdle, fetchVerifiedProgress]);

  return { addEvent, setPageInfo, flushEvents, consumeViewCredit, fetchVerifiedProgress };
}
